// Notifications sagas.
//
// Covers REST CRUD (list / mark-read / mark-all-read / preferences).
//
// Real-time delivery is NO LONGER handled here. It moved to the WebSocket
// gateway: the realtime saga (sagas/realtime/) subscribes to the user channel
// and dispatches the same RECEIVED action for every server-pushed
// notification (mapping warning/critical severities to a toast). The bell's
// existing ``sseConnect()`` dispatch is consumed by the realtime saga, so the
// UI is unchanged. See core/WEBSOCKETS.md.

import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import axios from 'axios';

import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { extractErrorMessage } from '../../../utils/apiResponseHandler';
import { Error as ErrorAction } from '../../actions/error/error';
import {
  LIST_REQUEST, fetchListResponse, fetchListFailure,
  MARK_READ_REQUEST, markReadSuccess, markReadFailure,
  MARK_ALL_READ_REQUEST, markAllReadDone, markAllReadFail,
  PREFS_CATALOG_REQUEST, fetchCatalogOk, fetchCatalogErr,
  PREFS_FETCH_REQUEST,   fetchPreferencesOk, fetchPreferencesErr,
  PREFS_UPDATE_REQUEST,  updatePreferencesOk, updatePreferencesErr,
  CHANNELS_FETCH_REQUEST, fetchChannelsOk, fetchChannelsErr,
  CHANNEL_SAVE_REQUEST,   saveChannelOk, saveChannelErr,
  CHANNEL_DELETE_REQUEST, deleteChannelOk, deleteChannelErr,
  CHANNEL_TEST_REQUEST,   testChannelResult,
  fetchChannels,
} from '../../actions/notifications';

const unwrap = (response) => response.data?.data ?? response.data;
const errMsg = extractErrorMessage;

// -------------------------------------------------------------------------
// REST sagas
// -------------------------------------------------------------------------

function* listSaga({ payload }) {
  try {
    const params = new URLSearchParams();
    if (payload?.unread)   params.set('unread', 'true');
    if (payload?.since)    params.set('since',  payload.since);
    if (payload?.per_page) params.set('per_page', payload.per_page);
    if (payload?.page)     params.set('page',    payload.page);
    const qs = params.toString();
    const url = `${config.API_URL}/notifications/${qs ? `?${qs}` : ''}`;
    const response = yield call(axios.get, url, { headers: getLocalUser() });
    yield put(fetchListResponse(unwrap(response)));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(fetchListFailure(errMsg(error)));
  }
}

function* markReadSaga({ payload }) {
  try {
    yield call(
      axios.post,
      `${config.API_URL}/notifications/${payload.notification_id}/read`,
      {},
      { headers: getLocalUser() },
    );
    yield put(markReadSuccess(payload.notification_id));
  } catch (error) {
    yield put(markReadFailure(errMsg(error)));
  }
}

function* markAllReadSaga() {
  try {
    yield call(
      axios.post,
      `${config.API_URL}/notifications/mark-all-read`,
      {},
      { headers: getLocalUser() },
    );
    yield put(markAllReadDone());
  } catch (error) {
    yield put(markAllReadFail(errMsg(error)));
  }
}

function* fetchCatalogSaga() {
  try {
    const response = yield call(
      axios.get,
      `${config.API_URL}/notifications/preferences/catalog`,
      { headers: getLocalUser() },
    );
    yield put(fetchCatalogOk(unwrap(response)));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(fetchCatalogErr(errMsg(error)));
  }
}

function* fetchPreferencesSaga() {
  try {
    const response = yield call(
      axios.get,
      `${config.API_URL}/notifications/preferences`,
      { headers: getLocalUser() },
    );
    yield put(fetchPreferencesOk(unwrap(response)));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(fetchPreferencesErr(errMsg(error)));
  }
}

function* updatePreferencesSaga({ payload }) {
  try {
    const response = yield call(
      axios.put,
      `${config.API_URL}/notifications/preferences`,
      payload,
      { headers: getLocalUser() },
    );
    yield put(updatePreferencesOk(unwrap(response)));
  } catch (error) {
    yield put(ErrorAction(error));
    yield put(updatePreferencesErr(errMsg(error)));
  }
}

// -------------------------------------------------------------------------
// Delivery channel endpoints (Slack / Teams / PagerDuty / webhook)
// -------------------------------------------------------------------------

function* fetchChannelsSaga() {
  try {
    const response = yield call(
      axios.get,
      `${config.API_URL}/notifications/channels`,
      { headers: getLocalUser() },
    );
    yield put(fetchChannelsOk(unwrap(response)));
  } catch (error) {
    yield put(fetchChannelsErr(errMsg(error)));
  }
}

function* saveChannelSaga({ payload }) {
  try {
    const { channel_id, ...body } = payload || {};
    const response = channel_id
      ? yield call(
          axios.put,
          `${config.API_URL}/notifications/channels/${channel_id}`,
          body,
          { headers: getLocalUser() },
        )
      : yield call(
          axios.post,
          `${config.API_URL}/notifications/channels`,
          body,
          { headers: getLocalUser() },
        );
    yield put(saveChannelOk(unwrap(response)));
    // Re-list so masked hints / ordering stay server-authoritative.
    yield put(fetchChannels());
  } catch (error) {
    yield put(saveChannelErr(errMsg(error)));
  }
}

function* deleteChannelSaga({ payload }) {
  try {
    yield call(
      axios.delete,
      `${config.API_URL}/notifications/channels/${payload.channel_id}`,
      { headers: getLocalUser() },
    );
    yield put(deleteChannelOk(payload.channel_id));
  } catch (error) {
    yield put(deleteChannelErr(errMsg(error)));
  }
}

function* testChannelSaga({ payload }) {
  try {
    const response = yield call(
      axios.post,
      `${config.API_URL}/notifications/channels/${payload.channel_id}/test`,
      {},
      { headers: getLocalUser() },
    );
    const data = unwrap(response) || {};
    yield put(testChannelResult(payload.channel_id, {
      ok: !!data.ok, detail: data.detail || '',
    }));
  } catch (error) {
    // BE returns 502 when the third party rejects the test post; the
    // response body still carries the detail string.
    const data = error?.response?.data?.data || error?.response?.data || {};
    yield put(testChannelResult(payload.channel_id, {
      ok: false, detail: data.detail || errMsg(error),
    }));
  }
}

// -------------------------------------------------------------------------
// Watcher
// -------------------------------------------------------------------------

export function* watch() {
  yield takeLatest(LIST_REQUEST, listSaga);
  yield takeEvery (MARK_READ_REQUEST, markReadSaga);
  yield takeLatest(MARK_ALL_READ_REQUEST, markAllReadSaga);
  yield takeLatest(PREFS_CATALOG_REQUEST, fetchCatalogSaga);
  yield takeLatest(PREFS_FETCH_REQUEST, fetchPreferencesSaga);
  yield takeLatest(PREFS_UPDATE_REQUEST, updatePreferencesSaga);
  yield takeLatest(CHANNELS_FETCH_REQUEST, fetchChannelsSaga);
  yield takeEvery (CHANNEL_SAVE_REQUEST, saveChannelSaga);
  yield takeEvery (CHANNEL_DELETE_REQUEST, deleteChannelSaga);
  yield takeEvery (CHANNEL_TEST_REQUEST, testChannelSaga);
}
